import type { FundPolicy } from '../../../core/fundPolicy/schema';
import {
  ALLOWED_MIME_TYPES,
  MAX_UPLOAD_BYTES,
  type DocumentCategory,
  type DocumentKind,
  type UploadedDocument,
} from './ports/documentPort';

/**
 * What the employee still has to upload, and whether a chosen file is
 * acceptable.
 *
 * Pure, so it can be tested without a browser or a network (§5.4).
 */

/**
 * Which category a kind belongs to.
 *
 * Derived rather than supplied by the caller: the category decides the storage
 * path and therefore who can read the file, so letting a screen pass it in
 * would let a medical report be filed where finance can see it.
 */
export function categoryFor(kind: DocumentKind): DocumentCategory {
  return kind === 'medical_report' ? 'medical' : 'identity';
}

export const DOCUMENT_LABELS_AR: Readonly<Record<DocumentKind, string>> = {
  employee_id: 'صورة الهوية',
  marriage_certificate: 'عقد الزواج',
  birth_certificate: 'شهادة الميلاد',
  medical_report: 'تقرير طبي',
};

export interface RequiredDocument {
  readonly kind: DocumentKind;
  readonly labelAr: string;
  /** Present when the requirement belongs to a specific dependant. */
  readonly dependentId: string | null;
  readonly forNameAr: string | null;
  readonly satisfied: boolean;
}

export interface ChecklistDependent {
  readonly id: string;
  readonly fullName: string;
  readonly relation: string;
}

/**
 * Builds the checklist from the *stored* policy plus the dependants entered.
 *
 * The confirmed requirements are: an identity document for the employee, a
 * marriage certificate for a spouse, and a birth certificate per child. Which
 * relations need proof at all comes from the policy, so turning that off for a
 * relation removes its row without a code change.
 */
export function buildDocumentChecklist(
  policy: FundPolicy,
  dependents: readonly ChecklistDependent[],
  uploaded: readonly UploadedDocument[],
): readonly RequiredDocument[] {
  const has = (kind: DocumentKind, dependentId: string | null): boolean =>
    uploaded.some(
      (document) => document.kind === kind && document.dependentId === dependentId,
    );

  const required: RequiredDocument[] = [
    {
      kind: 'employee_id',
      labelAr: DOCUMENT_LABELS_AR.employee_id,
      dependentId: null,
      forNameAr: null,
      satisfied: has('employee_id', null),
    },
  ];

  for (const dependent of dependents) {
    const rule = policy.dependentRules.find((entry) => entry.relation === dependent.relation);
    if (!rule?.requiresProofDocument) continue;

    const kind: DocumentKind =
      dependent.relation === 'spouse' ? 'marriage_certificate' : 'birth_certificate';

    required.push({
      kind,
      labelAr: DOCUMENT_LABELS_AR[kind],
      dependentId: dependent.id,
      forNameAr: dependent.fullName,
      satisfied: has(kind, dependent.id),
    });
  }

  return required;
}

export type FileRejection = 'too_large' | 'wrong_type';

/**
 * Checks a chosen file before any upload starts.
 *
 * The storage rules check the same two things server-side. Checking here as
 * well is not duplication for its own sake: it stops someone watching a 40 MB
 * photo upload over a phone connection only to be refused at the end.
 */
export function rejectFile(file: {
  readonly size: number;
  readonly type: string;
}): FileRejection | null {
  if (file.size > MAX_UPLOAD_BYTES) return 'too_large';
  if (!ALLOWED_MIME_TYPES.includes(file.type)) return 'wrong_type';
  return null;
}

export const FILE_REJECTION_MESSAGES_AR: Readonly<Record<FileRejection, string>> = {
  too_large: 'حجم الملف أكبر من 10 ميجابايت. صوّر بجودة أقل أو اقتصّ الصورة.',
  wrong_type: 'صيغة الملف غير مقبولة. استخدم صورة (JPG أو PNG) أو ملف PDF.',
};

/** Human-readable size, for showing what was uploaded. */
export function formatBytes(bytes: number): string {
  if (bytes < 1024) return `${String(bytes)} بايت`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(0)} ك.ب`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} م.ب`;
}
